import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';

import { fmhAPI, TREATMENT_API_URL } from 'api/APIBase';

/**
 * Reinitiate a treatment.
 */
export async function reinitiateTreatment(payerId, treatmentId) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/reinitiate`;
    return fmhAPI.base.url(url).post().json();
}

/**
 * Retrieve a referral.
 */
export async function getReferral(referralId, payerId) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/referrals/${referralId}`;
    return fmhAPI.base
        .url(url)
        .get()
        .notFound(() => ({}))
        .json();
}

export async function unmanageTreatment({
    payerId,
    treatmentId,
    effectiveDate,
    username,
    reason,
    source,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/unmanage`;
    return fmhAPI.base
        .url(url)
        .post({
            effective_date: effectiveDate,
            username,
            reason,
            source,
        })
        .json();
}

export async function remanageTreatment({ payerId, treatmentId, overrideBenefitCheck }) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/remanage`;
    return fmhAPI.base
        .url(url)
        .query({
            override_benefit_check: !!overrideBenefitCheck,
        })
        .post()
        .json();
}

export async function transferTreatment({
    payerId,
    treatmentId,
    username,
    reason,
    source,
    newServicingPharmacyNpi,
    offerRate,
    referentPriceType,
    priceSource,
    isFrozen,
    routeCode,
    forceTransferRoute,
}) {
    const getOfferRateObject = (rate) => {
        if (rate === null) {
            return null;
        }
        if (rate === CONTRACT_RATE) {
            return {
                rate,
                referent_price_type: referentPriceType ?? ReferentPriceTypes.AWP,
                price_source: priceSource ?? 'MEDISPAN',
                price_is_frozen: isFrozen ?? false,
            };
        }
        return {
            rate,
            referent_price_type: referentPriceType,
            price_source: priceSource,
            price_is_frozen: isFrozen,
        };
    };

    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/transfer`;
    return fmhAPI.base
        .url(url)
        .post({
            username,
            reason,
            source,
            new_servicing_pharmacy_npi: newServicingPharmacyNpi,
            offer_rate: getOfferRateObject(offerRate),
            route_code: routeCode,
            allow_route_override: !forceTransferRoute,
        })
        .json();
}

export async function discontinueTreatment({
    payerId,
    treatmentId,
    effectiveDate,
    username,
    reason,
    source,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/discontinue`;
    return fmhAPI.base
        .url(url)
        .post({
            effective_date: effectiveDate,
            username,
            reason,
            source,
        })
        .json();
}

/** Update the product type for a referral */
export async function updateProductType({
    treatmentId,
    payerId,
    updatedIsBrand,
    routeCode,
    offerRateOverride,
    referentPriceType,
    priceSource,
    isFrozen,
    username,
    reason,
    source,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/product_type`;
    return fmhAPI.base
        .url(url)
        .post({
            new_is_brand: updatedIsBrand,
            route_code: routeCode,
            offer_rate: {
                rate: offerRateOverride,
                referent_price_type: referentPriceType,
                price_source: priceSource,
                price_is_frozen: isFrozen,
            },
            username,
            reason,
            source,
        })
        .json();
}

export async function transitionTreatment({
    payerId,
    treatmentId,
    transitionDate,
    reason,
    source,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/transition`;
    return fmhAPI.base
        .url(url)
        .post({
            transition_date: transitionDate,
            reason,
            source,
        })
        .json();
}

export async function changeReferralPeriodForTreatment({
    payerId,
    treatmentId,
    referralExpiration,
    reason,
    source,
    setToGrandfathered,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/change_referral_period`;
    return fmhAPI.base
        .url(url)
        .post({
            referral_expiration: referralExpiration,
            reason,
            source,
            set_to_grandfathered: setToGrandfathered || undefined,
        })
        .json();
}

export async function updatePlaceOfService({
    payerId,
    treatmentId,
    reason,
    source,
    placeOfService,
}) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/place_of_service`;
    return fmhAPI.base
        .url(url)
        .post({
            reason,
            source,
            new_place_of_service: placeOfService,
        })
        .json();
}

export async function updateProductServiceId({ payerId, treatmentId, productServiceId }) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/product_service_id`;
    return fmhAPI.base
        .url(url)
        .post({
            new_product_service_id: productServiceId,
        })
        .json();
}

/** Update prescriber data on treatment */
export async function updatePrescriber({ payerId, treatmentId, prescriberNpi, reason, source }) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/${treatmentId}/update_prescriber/${prescriberNpi}`;
    return fmhAPI.base.url(url).put({ reason, source }).json();
}

export async function blocklistTreatment({ payerId, treatmentId, username, reason }) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/blocklist/${treatmentId}`;
    return fmhAPI.base
        .url(url)
        .post({
            username,
            reason,
        })
        .json();
}

export async function unblocklistTreatment({ payerId, treatmentId }) {
    const url = `${TREATMENT_API_URL}/admin/${payerId}/treatments/blocklist/${treatmentId}`;
    return fmhAPI.base.url(url).delete();
}
